/**
 * Mapeo de entidades del backend (PermissionEntity) a elementos del frontend.
 *
 * Este archivo es la "tabla de verdad" que conecta:
 *   - Backend: entity string (ej. "Countries")
 *   - Frontend: ruta de navegación, viewKey, etiqueta legible, categoría
 *
 * El menú se filtra dinámicamente según los permisos READ del usuario.
 * Las acciones (crear, editar, eliminar) se filtran según WRITE / DELETE.
 */

export interface EntityMapping {
  entity: string;
  label: string;
  category: "masters" | "operations" | "finance" | "imports" | "config" | "sub-resource" | "other";
  /** Ruta de navegación principal (null = sub-recurso sin ruta propia) */
  href: string | null;
  /** viewKey del GenericMasterView (si aplica) */
  viewKey?: string;
  /** Entidad padre para sub-recursos */
  parentEntity?: string;
}

export const ENTITY_MAP: EntityMapping[] = [
  // ─── Maestros ───────────────────────────────────────────────────────────
  { entity: "Countries",              label: "Países",                  category: "masters",   href: "/maestros/paises",            viewKey: "countries-list" },
  { entity: "Carriers",               label: "Navieras",                category: "masters",   href: "/maestros/navieras",          viewKey: "carriers-list" },
  { entity: "Contacts",               label: "Contactos",               category: "masters",   href: "/maestros/contactos",         viewKey: "contacts-list" },
  { entity: "Entities",               label: "Entidades",               category: "masters",   href: "/maestros/entidades",         viewKey: "entities-list" },
  { entity: "Equipment",              label: "Equipos",                 category: "masters",   href: "/maestros/equipos",           viewKey: "equipment-list" },
  { entity: "Terminals",              label: "Terminales / Puertos",    category: "masters",   href: "/maestros/puertos",           viewKey: "ports-list" },
  { entity: "Incoterms",              label: "Incoterms",               category: "masters",   href: null },
  { entity: "ContractChargeConcepts", label: "Conceptos de Tarifa",     category: "masters",   href: "/maestros/conceptos-tarifa",  viewKey: "tariff-concepts-list" },
  { entity: "CarrierInvoiceConcepts", label: "Conceptos de Factura",    category: "masters",   href: "/maestros/conceptos",         viewKey: "invoice-concepts-list" },
  { entity: "VatRules",               label: "Reglas de IVA",           category: "masters",   href: "/maestros/reglas-iva",        viewKey: "vat-rules-list" },
  { entity: "Nomenclators",           label: "Nomencladores",           category: "masters",   href: "/maestros/nomencladores",     viewKey: "nomenclators-list" },
  { entity: "EntityAliases",          label: "Mapeo de Maestros",       category: "masters",   href: "/maestros/mapeo-maestros",    viewKey: "entity-aliases-list" },
  { entity: "TerminalAliases",        label: "Alias de Terminales",     category: "masters",   href: "/maestros/alias-terminales",  viewKey: "terminal-aliases-list" },
  { entity: "EquipmentTypeAliases",   label: "Alias de Equipos",        category: "masters",   href: "/maestros/alias-equipos",     viewKey: "equipment-type-aliases-list" },

  // ─── Operaciones ────────────────────────────────────────────────────────
  { entity: "Contracts",              label: "Contratos",               category: "operations", href: "/contratos",                 viewKey: "contracts-list" },
  { entity: "ContractLines",          label: "Líneas de Contrato",      category: "sub-resource", href: null, parentEntity: "Contracts" },
  { entity: "ContractLineCompanies",  label: "Compañías de Línea",      category: "sub-resource", href: null, parentEntity: "Contracts" },
  { entity: "ContractLineCharges",    label: "Tarifas",                 category: "operations", href: "/tarifas",                   viewKey: "line-charges-list" },
  { entity: "Bookings",               label: "Reservas",                category: "operations", href: "/bookings",                  viewKey: "bookings-list" },
  { entity: "Bookings",               label: "Salud Conciliacion",      category: "operations", href: "/bookings/reconciliation-health" },
  { entity: "BookingEquipment",       label: "Equipos de Booking",      category: "sub-resource", href: null, parentEntity: "Bookings" },
  { entity: "BookingStatusEvents",    label: "Eventos de Estado",       category: "sub-resource", href: null, parentEntity: "Bookings" },
  { entity: "SavedViews",             label: "Vistas Guardadas",        category: "sub-resource", href: null },

  // ─── Finanzas ───────────────────────────────────────────────────────────
  { entity: "Currencies",             label: "Monedas",                 category: "finance",   href: "/maestros/monedas",           viewKey: "currencies-list" },
  { entity: "CurrencyRates",          label: "Tipos de Cambio",         category: "sub-resource", href: null, parentEntity: "Currencies" },
  { entity: "CarrierInvoices",        label: "Facturas de Proveedores", category: "finance",   href: "/finanzas/facturas",          viewKey: "supplier-invoice-lines-list" },
  { entity: "CarrierInvoiceLines",    label: "Líneas Factura Prov.",    category: "sub-resource", href: null, parentEntity: "CarrierInvoices" },
  { entity: "InternalInvoices",       label: "Facturas Internas",       category: "finance",   href: "/finanzas/facturas-internas", viewKey: "internal-invoice-lines-list" },
  { entity: "InternalInvoiceLines",   label: "Líneas Factura Int.",     category: "sub-resource", href: null, parentEntity: "InternalInvoices" },

  // ─── Importaciones ─────────────────────────────────────────────────────
  { entity: "ImportFiles",            label: "Importaciones",           category: "imports",   href: "/importaciones",              viewKey: "imports-list" },
  { entity: "ImportAudit",            label: "Auditoría Import.",       category: "sub-resource", href: null, parentEntity: "ImportFiles" },
  { entity: "ProcessingLogs",         label: "Logs de Procesamiento",   category: "sub-resource", href: null, parentEntity: "ImportFiles" },
  { entity: "ProcessingErrors",       label: "Errores de Procesamiento",category: "sub-resource", href: null, parentEntity: "ImportFiles" },
  { entity: "InvoiceImports",         label: "Import. Facturas",        category: "sub-resource", href: null, parentEntity: "ImportFiles" },
  { entity: "ContractImports",        label: "Import. Contratos",       category: "sub-resource", href: null, parentEntity: "ImportFiles" },
  { entity: "Documents",              label: "Documentos",              category: "imports",   href: null },

  // ─── Configuración ─────────────────────────────────────────────────────
  { entity: "Users",                  label: "Usuarios",                category: "config",    href: "/configuracion/usuarios",     viewKey: "users-list" },
];

/**
 * Mapeo de rutas del menú principal a la entidad que controla su visibilidad.
 * Si la ruta no está aquí, se muestra siempre (ej. Dashboard).
 */
/** Sentinela: ruta reservada al rol SUPERADMIN, evaluada por rol, no por grants. */
export const SUPERADMIN_ONLY = "__SUPERADMIN_ONLY__";

export const ROUTE_TO_ENTITY: Record<string, string> = {
  "/contratos":                   "Contracts",
  "/tarifas":                     "ContractLineCharges",
  "/bookings":                    "Bookings",
  "/bookings/reconciliation-health": "Bookings",
  "/finanzas/facturas":           "CarrierInvoices",
  "/finanzas/facturas-internas":  "InternalInvoices",
  "/importaciones":               "ImportFiles",
  "/maestros":                    "__ANY_MASTER__",
  "/maestros/paises":             "Countries",
  "/maestros/navieras":           "Carriers",
  "/maestros/monedas":            "Currencies",
  "/maestros/equipos":            "Equipment",
  "/maestros/nomencladores":      "Nomenclators",
  "/maestros/puertos":            "Terminals",
  "/maestros/conceptos":          "CarrierInvoiceConcepts",
  "/maestros/reglas-iva":         "VatRules",
  "/maestros/contactos":          "Contacts",
  "/maestros/conceptos-tarifa":   "ContractChargeConcepts",
  "/maestros/mapeo-maestros":     "EntityAliases",
  "/maestros/alias-terminales":   "TerminalAliases",
  "/maestros/alias-equipos":      "EquipmentTypeAliases",
  "/maestros/tratamiento-navieras": "Nomenclators",
  "/maestros/entidades":          "Entities",
  "/configuracion/usuarios":      "Users",
  "/mis-permisos":                SUPERADMIN_ONLY,
  "/configuracion/roles":         SUPERADMIN_ONLY,
  "/configuracion/permisos":      SUPERADMIN_ONLY,
};

const MASTER_ENTITIES = [
  "Countries", "Carriers", "Contacts", "Entities", "Equipment", "Terminals",
  "ContractChargeConcepts", "CarrierInvoiceConcepts", "Nomenclators",
  "EntityAliases", "TerminalAliases", "EquipmentTypeAliases", "Currencies",
  "VatRules",
];

/**
 * Comprueba si el usuario tiene acceso READ a una ruta.
 * @param href  Ruta del menú
 * @param userEntities  Set de entidades para las que el usuario tiene READ
 * @param isSuperadmin  Si el usuario tiene rol SUPERADMIN. Por defecto `false`
 *   (valor seguro: si una llamada olvida pasarlo, las rutas SUPERADMIN_ONLY
 *   quedan ocultas, nunca expuestas).
 */
export function canAccessRoute(href: string, userEntities: Set<string>, isSuperadmin = false): boolean {
  const entity = ROUTE_TO_ENTITY[href];
  if (!entity) return true;
  if (entity === SUPERADMIN_ONLY) return isSuperadmin;
  if (entity === "__ANY_MASTER__") {
    return MASTER_ENTITIES.some((e) => userEntities.has(e));
  }
  return userEntities.has(entity);
}

/** Devuelve el mapping para una entidad */
export function getEntityMapping(entity: string): EntityMapping | undefined {
  return ENTITY_MAP.find((m) => m.entity === entity);
}

/** Devuelve la entidad backend asociada a un viewKey del frontend */
export function getEntityForViewKey(viewKey: string): string | null {
  const mapping = ENTITY_MAP.find((m) => m.viewKey === viewKey);
  return mapping?.entity ?? null;
}

/** Devuelve solo las entidades con ruta propia (no sub-recursos) */
export function getNavigableEntities(): EntityMapping[] {
  return ENTITY_MAP.filter((m) => m.href !== null && m.category !== "sub-resource");
}

/** Agrupar entidades por categoría */
export function getEntitiesByCategory(): Record<string, EntityMapping[]> {
  const groups: Record<string, EntityMapping[]> = {};
  for (const m of ENTITY_MAP) {
    if (!groups[m.category]) groups[m.category] = [];
    groups[m.category].push(m);
  }
  return groups;
}

/**
 * Igual que getEntitiesByCategory pero deduplica entidades con el mismo nombre
 * dentro de cada categoría (ej: "Countries" aparece como "Países" y "Puertos").
 * Combina los labels: "Países / Puertos".
 */
export function getDeduplicatedEntitiesByCategory(): Record<string, EntityMapping[]> {
  const groups = getEntitiesByCategory();
  const result: Record<string, EntityMapping[]> = {};
  for (const [cat, entries] of Object.entries(groups)) {
    const seen = new Map<string, EntityMapping>();
    for (const m of entries) {
      const existing = seen.get(m.entity);
      if (existing) {
        // Combinar labels
        seen.set(m.entity, { ...existing, label: `${existing.label} / ${m.label}` });
      } else {
        seen.set(m.entity, { ...m });
      }
    }
    result[cat] = [...seen.values()];
  }
  return result;
}

export const CATEGORY_LABELS: Record<string, string> = {
  masters: "Maestros",
  operations: "Operaciones",
  finance: "Finanzas",
  imports: "Importaciones",
  config: "Configuración",
  "sub-resource": "Sub-recursos",
  other: "Otros (no mapeados)",
};

// ─── Generación de descripciones y feComponentKey ───────────────────────────

const ACTION_VERB: Record<string, string> = {
  READ: "Ver",
  WRITE: "Crear y editar",
  DELETE: "Eliminar",
};

/**
 * Genera la descripción en español para una combinación entity+action.
 * Ej: "Countries" + "READ" → "Ver países"
 */
export function generateDescription(entity: string, action: string): string {
  const mapping = ENTITY_MAP.find((m) => m.entity === entity);
  const label = mapping?.label ?? entity;
  const verb = ACTION_VERB[action] ?? action;
  return `${verb} ${label.toLowerCase()}`;
}

/**
 * Genera el feComponentKey para una combinación entity+action.
 * Usa el viewKey del mapping si existe, o el entity en lowercase.
 * Ej: "Countries" + "READ" → "countries-list.read"
 *     "Bookings" + "WRITE" → "bookings-list.write"
 */
export function generateFeComponentKey(entity: string, action: string): string {
  const mapping = ENTITY_MAP.find((m) => m.entity === entity);
  const base = mapping?.viewKey ?? entity.toLowerCase();
  return `${base}.${action.toLowerCase()}`;
}

/**
 * Genera la ruta de navegación asociada a una entidad.
 * Se usa para asociar permisos a vistas del frontend.
 */
export function getEntityRoute(entity: string): string | null {
  const mapping = ENTITY_MAP.find((m) => m.entity === entity);
  return mapping?.href ?? null;
}

/**
 * Devuelve un mapa de entidad padre → sub-recursos hijos.
 * Solo incluye entidades que tienen parentEntity definido.
 */
export function getChildrenByParent(): Map<string, EntityMapping[]> {
  const map = new Map<string, EntityMapping[]>();
  for (const m of ENTITY_MAP) {
    if (m.parentEntity) {
      const children = map.get(m.parentEntity) ?? [];
      children.push(m);
      map.set(m.parentEntity, children);
    }
  }
  return map;
}

/**
 * Devuelve las entidades de una categoría ordenadas: padre seguido de sus hijos.
 * Los sub-recursos se excluyen como items top-level y se anidan bajo su padre.
 */
export function getEntitiesWithChildren(
  categoryEntities: EntityMapping[],
): { parent: EntityMapping; children: EntityMapping[] }[] {
  const childrenMap = getChildrenByParent();
  const subResourceEntities = new Set(
    ENTITY_MAP.filter((m) => m.category === "sub-resource").map((m) => m.entity),
  );
  const result: { parent: EntityMapping; children: EntityMapping[] }[] = [];
  for (const m of categoryEntities) {
    if (subResourceEntities.has(m.entity)) continue; // se muestra bajo su padre
    const children = childrenMap.get(m.entity) ?? [];
    result.push({ parent: m, children });
  }
  return result;
}
